import { Badge } from '@edsn/pie-components';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Comment } from '#pie/components/comment/Comment';
import { Text } from '#pie/components/text/Text';
import type { GetCommentParams } from '#pie/internal/api/pie-bff';
import { useGetComment } from '#pie/internal/api/pie-bff';
import { tv } from '#pie/utils/TailwindUtils';
import { Divider } from '../divider/Divider';
import { ErrorMessageBlock } from '../error-message/ErrorMessageBlock';
import { ReplyField } from '../reply-field/ReplyField';
import { Stack, StackItem } from '../stack/Stack';

interface CommentsProps
  extends GetCommentParams,
    Pick<ComponentProps<typeof ReplyField>, 'canAddAttachments'> {
  canComment?: boolean;
}

export function Comments({ canComment, ...commentParams }: CommentsProps) {
  const { data, error, isLoading } = useGetComment(commentParams);
  const { t } = useTranslation('ticket');
  if (error) {
    return <ErrorMessageBlock error={error} />;
  }

  const hasComments = !!(data?.items && data.items.length > 0);

  const s = comments();

  return isLoading || (data && data.totalCount > 0) || canComment ? (
    <section className={s.root()}>
      {canComment && <ReplyField {...commentParams} />}
      <div className={s.header()}>
        <Divider className="my-2" />
        <div className={s.titleRow()}>
          <Text variant="h6" className="text-content-primary">
            {t('comment.replies')}
          </Text>
          <Badge size="sm">{data?.totalCount}</Badge>
        </div>
      </div>

      {(hasComments || isLoading) && (
        <>
          <Stack as="ul" gap="sm" divider="line">
            {isLoading
              ? new Array(3).fill(null).map((_, index) => (
                  <StackItem as="li" key={`comment${index}`}>
                    <Comment isLoading />
                  </StackItem>
                ))
              : data?.items?.map((comment) => (
                  <StackItem as="li" key={comment.id}>
                    <Comment comment={comment} />
                  </StackItem>
                ))}
          </Stack>
          <Divider />
        </>
      )}
    </section>
  ) : null;
}

export const comments = tv({
  slots: {
    root: 'flex flex-col gap-4 px-5 py-5 bg-white radius-card-default',
    header: 'flex flex-col',
    titleRow: 'flex flex-row gap-3 items-center mt-4',
  },
});
